/**
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import Icon from 'qwc2/components/Icon';
import ApiUrl from 'qwc2/plugins/ApiUrl';

export function renderHelp() {
return (<div id="Help"><br />
    <h1>
        Sweco karttapalvelu
    </h1>
    <p>Versio: {process.env.BuildDate}</p>
    <b>
        Karttapalvelun ylläpito: <a href="mailto:karttapalvelu@sweco.fi">karttapalvelu@sweco.fi</a>
    </b>
    <p>
        Karttapalvelussa voit yhdistää tietoja ja ominaisuuksia karttatasojen
        avulla, luoda omia karttanäkymiäsi esimerkiksi teemakartan tyylisesti,
        jakaa ja tallentaa niitä, sekä tehdä yksinkertaisia analyyseja. Samalla
        linkillä pääset sekä työpöytä että mobiiliversioihin. Palvelun kielen voi
        vaihtaa toistaiseksi vain selaimen asetuksista. Parhaaseen lopputulokseen
        pääset uusimmalla <b>Google Chrome -selaimella.</b>
    </p>
    <ApiUrl/>
    <br/>
    <h3>
        Käyttöohjeet (<a href="#svenska">Anvisningar på svenska</a>)
    </h3>
    <h2>
        Ohjeen sisältö
    </h2>
    <p>
        1. Karttanäkymä
    </p>
    <p>
        2. Haku
    </p>
    <p>
        3. Karttatasot
    </p>
    <p>
        4. Karttatyökalut
    </p>
    <p>
        5. Tulosta
    </p>
    <p>
        6. Jaa linkki
    </p>
    <p>
        7. Koordinaatisto
    </p>
    <h2>
        1. ​Karttanäkymä
    </h2>
    <p>
        ​[Kuva käyttöliittymästä ja nuolet osoittamaan tärkeimpiin ominaisuuksiin]
    </p>
    <p>
        Karttanäkymän yläpalkista löydät kohteiden hakuominaisuuden ja yleisimpiä
        työkaluja. Oikeasta alakulmasta löytyy zoomauspainikkeet sekä kotinäppäin,
        jolla pääset takaisin aloitusnäkymään. Oikeassa alakulmassa olevasta paikan
        nimen linkistä pääset kaupungin/kunnan nettisivuille. Vasemmassa
        yläkulmassa on ”Kartat &amp; Työkalut” osio, josta löytyy karttatasot,
        linkin jakaminen, kirjanmerkin määrittäminen, karttatyökalut, sekä
        tulostus- ja apupainikkeet. Vasemman yläkulman nuolipainikkeesta avautuu
        karttatasolistaus. Karttanäkymän alalaidassa on harmaa palkki, jossa on
        koordinaatti- ja mittakaavatietoja, sekä käyttöehdot.
    </p>
    <p>
        <strong>Kartalla liikkuminen</strong>
        <strong></strong>
    </p>
    <p>
        Kartta liikkuu painamalla hiirellä karttanäkymää ja raahaamalla hiirtä.
    </p>
    <p>
        <strong>Paikanna, Lähennä, loitonna tai palaa aloitusnäkymään</strong>
        <strong></strong>
    </p>
    <p>
        Laitteen GPS-sijainti saadaan näkyviin paikanna-painikkeesta.
        <Icon icon="plus" size="large"/>
    </p>
    <p>
        Rullaamalla hiirtä voi lähentyä tai loitontua karttanäkymässä.
    </p>
    <p>
        Toinen vaihtoehto on käyttää pikakuvaketta <Icon icon="plus" size="large"/> tai
        <Icon icon="minus" size="large"/> lähentämiseen tai loitontamiseen.
    </p>
    <p>
        Aloitusnäkymään pääsee kotipainikkeen <Icon icon="home" size="large"/> kautta.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Karttanäkymän klikkaaminen</strong>
        <strong></strong>
    </p>
    <p>
        Hiiren vasemmalla painikkeella saat esille kohteen tietoja.
    </p>
    <p>
        Hiiren oikealla painikkeella saat näkyviin kursorin osoittaman kohdan
        koordinaatit.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Karttatason vaihtaminen</strong>
        <strong></strong>
    </p>
    <p>
        Karttatasot löytyvät pikakuvakkeen [layers and legend] takaa.
    </p>
    <p>
        Karttatason selitteen kohdalta voi aktivoida ja/tai piilottaa tasoja.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Kohteiden haku</strong>
        <strong></strong>
    </p>
    <p>
        Karttanäkymän yläpalkissa on hakuominaisuus, jolla voi etsiä kohteita.
    </p>
    <p>
        Näkymä lähentyy kohdetta valinnan jälkeen.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Mittaaminen</strong>
        <strong></strong>
    </p>
    <p>
        Etäisyyden mittaamista varten on pikakuvake <Icon icon="measure_line" size="large"/>.
    </p>
    <p>
        Pinta-alan laskemista varten on pikakuvake <Icon icon="measure_polygon" size="large"/>.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Koordinaatit</strong>
        <strong></strong>
    </p>
    <p>
        Karttanäkymän alhaalla olevassa palkissa näkyvät koordinaatit,
        koordinaattijärjestelmä ja mittakaava.
    </p>
    <p>
        Painamalla hiiren oikealla painikkeella karttanäkymään avautuu kursorin
        osoittaman kohdan koordinaatit GK- ja WSG84 koordinaatistoissa.
    </p>
    <p>
        Lisätietoja työkaluista löytyy ohjeen osiosta 4. Karttatyökalut.
    </p>
    <h2>
        2. Haku
    </h2>
    <p>
        Hakutoiminto löytyy karttaliittymän yläpalkista. Haettavia kohteita on
        useita erilaisia muun muassa tiet, koulut ja liikuntakohteet.
    </p>
    <p>
        1. Kirjoittamalla hakusanan kenttään järjestelmä ehdottaa kohteita tai
        kohteen.
    </p>
    <p>
        2. Valitsemalla kohteen karttanäkymä siirtyy ja lähentyy haettavaa
        kohdetta. Jos kohteita ei löydy avainsanan perusteella, järjestelmä ei anna
        tuloksia.
    </p>
    <h2>
        3. Karttatasot
    </h2>
    <p>
        Karttatasoihin pääsee karttanäkymän vasemmasta yläreunasta, joko suoraan
        nuolimaisen kuvakkeen <Icon icon="karttatasot-nuoli" size="medium"/> kautta, tai ”Kartat &amp; Työkalut”
        valikon kautta. Tasoja voi aktivoida (sulattaa) ja piilottaa (jäädyttää)
        ”rasti ruutuun” menetelmällä. Tasojen paikkaa ja läpinäkyvyyttä voi hallita
        <Icon icon="cog" size="large"/> kuvakkeesta. Samasta paikasta löytyy tasoon
        liittyviä lisätietoja. Valitut tasot näkyvät kartalla ja aktiiviset tasot voidaan suodattaa
        <Icon icon="eye" size="large"/> kuvakkeesta.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Karttatason vaihtaminen</strong>
    </p>
    <p>
        1. Avaa karttatasot -välilehti <Icon icon="karttatasot-nuoli" size="medium"/> kuvakkeen tai ”Kartat
        &amp; Työkalut” valikon kautta.
    </p>
    <p>
        2. Aktivoi tai piilota taso ”rasti ruutuun” menetelmällä aineiston
        vasemmalla puolella olevaa laatikkoa painamalla.
    </p>
    <p>
        <strong>Karttatason hallinta</strong>
    </p>
    <p>
        1. Valitse ja aktivoi taso.
    </p>
    <p>
        2. Tason oikealla puolella on <Icon icon="cog" size="large"/> kuvake, jota painamalla
        saa esille läpinäkyvyyden hallintasäätimen. Tasoa voi liikuttaa listassa
        alas tai ylöspäin nuolipainikkeilla ja tasoon liittyviä lisätietoja löytyy
        infopainikkeesta.
    </p>
    <p>
        3. Valittuja aineistoja voi myös hallita <Icon icon="eye" size="large"/> kuvakkeen
        takaa.
    </p>
    <h2>
        4. Karttatyökalut
    </h2>
    <p>
        Karttatyökalut löytyvät ”Kartat &amp; Työkalut” valikosta. Karttanäkymässä
        löydät osan työkaluista yläpalkista pikakuvakkeena. Karttatyökalujen avulla
        voi rajata alueen kohteiden/tietojen hakemiseen, tehdä mittauksia ja
        merkintöjä kartalle, sekä ladata aineistoa rasterina.
    </p>
    <h3>
        <em><Icon icon="identify_region" size="large"/> Tunnista alue:</em>
    </h3>
    <p>
        Työkalulla luodaan polygoni halutulle alueelle. Polygonin luonnin jälkeen
        avautuvalla välilehdellä näkyy polygonin rajaamat kohteet, jotka voidaan
        ladata halutussa muodossa.
    </p>
    <p>
        Käyttöohje:
    </p>
    <p>
        1. Aktivoi työkalu.
    </p>
    <p>
        2. Paina vasenta hiirenpainiketta luodaksesi polygonin pisteet.
    </p>
    <p>
        3. Polygonin viimeinen piste luodaan kaksoisklikkaamalla hiirtä.
    </p>
    <p>
        4. Avautuu välilehti, jossa on kohteiden ominaisuustiedot.
    </p>
    <h3>
        <em><Icon icon="measure" size="large"/> Mittaa:</em>
    </h3>
    <p>
        Mittaa kuvakkeesta avautuu uusi ikkuna karttanäkymän yläosaan. Valitsemalla
        halutun neljästä työkalusta voidaan kartalta osoittaa koordinaattipisteitä,
        laskea pituuksia, pinta-aloja ja suuntia.
    </p>
    <p>
        <em><strong>Sijainti</strong></em>
        <em>:</em>
    </p>
    <p>
        ​​​​​Laskee ja muodostaa karttanäkymään pisteobjektin koordinaattien
        kanssa.
    </p>
    <p>
        Käyttöohje:
    </p>
    <p>
        1. Aktivoi sijainti -työkalu.
    </p>
    <p>
        2. Paina karttanäkymässä vasemmalla hiirenpainikkeella.
    </p>
    <p>
        3. Karttanäkymään muodostuu pisteobjekti koordinaatteineen.
    </p>
    <p>
        <em><strong>Pituus</strong></em>
        <em>:</em>
    </p>
    <p>
        Muodostaa murtoviivan (polyline) ja laskee sen kokonaispituuden. Pituuden
        yksiköksi voi valita m, km, ft tai mi. Karttanäkymässä ilmoitetaan
        yksittäisten segmenttien pituus ja yläosan työkaluvalikossa murtoviivan
        kokonaispituus.
    </p>
    <p>
        Käyttöohje:
    </p>
    <p>
        1. Aktivoi pituus -työkalu.
    </p>
    <p>
        2. Luo murtoviivan alku- ja taitepisteet vasemmalla hiirenpainikkeella.
    </p>
    <p>
        3. Loppupiste muodostuu kaksoisklikkaamalla karttanäkymää.
    </p>
    <p>
        <em><strong>Alue</strong></em>
        <em>:</em>
    </p>
    <p>
        Työkalulla muodostetaan polygoni, jonka pinta-alan järjestelmä laskee.
        Tulokset näkyvät työkaluvalikossa sekä polygonin keskellä karttanäkymässä.
        Valittavat yksiköt ovat m<sup>2</sup>, km<sup>2</sup>, ft<sup>2</sup> tai
        mi<sup>2</sup>.
    </p>
    <p>
        ​​​Käyttöohje:
    </p>
    <p>
        1. Aktivoi alue -työkalu.
    </p>
    <p>
        2. Luo alku- ja välipisteet vasemmalla hiirenpainikkeella.
    </p>
    <p>
        3. Loppupiste luodaan kaksoisklikkaamalla karttanäkymää.
    </p>
    <p>
        <em><strong>Suunta</strong></em>
        <em>:</em>
    </p>
    <p>
        Työkalulla luodaan murtoviiva, jonka ilmansuunnan järjestelmä laskee.
        Tulokset ilmestyvät työkaluvalikkoon sekä karttanäkymään.
    </p>
    <p>
        Käyttöohje:
    </p>
    <p>
        1. Aktivoi suunta -työkalu.
    </p>
    <p>
        2. Murtoviivan alku- ja loppupiste muodostetaan painamalla vasenta
        hiirenpainiketta.
    </p>
    <h3>
        <em><Icon icon="draw" size="large"/> Omat merkinnät:</em>
    </h3>
    <p>
        Työkalun ikkunasta voi lisätä karttanäkymään omia merkintöjä. Työkalulla
        pystyy piirtämään erilaisia geometrioita tai kirjoittamaan tekstiä, joiden
        värin ja koon voi määrittää erikseen. Omista merkinnöistä voi luoda uusia
        tasoja, mutta myös valmiita tasoja pystyy editoimaan.
    </p>
    <p>
        Käyttöohje:
    </p>
    <p>
        1. Avaa omat merkinnät -työkalu.
    </p>
    <p>
        2. Määritä tyylit ja luo piste-, viiva-, alue-, ympyrä- tai tekstiobjekti.
        Editoimalla voit valita, poistaa tai luoda bufferin objektille.
    </p>
    <p>
        3. <Icon icon="plus" size="medium"/> kuvaketta painamalla saat luotua objektille
        uuden tason.
    </p>
    <h3>
        <em><Icon icon="rasterexport" size="large"/> Rasterin vienti</em>
    </h3>
    <p>
        Määrittämällä halutun kokoisen suorakaiteen, alueesta muodostetaan rasteri
        halutussa formaatissa.
    </p>
    <p>
        Käyttöohje:
    </p>
    <p>
        1. Avaa rasteri -työkalu, jolloin karttanäkymä tummenee.
    </p>
    <p>
        2. Valitse formaatti ja resoluutio.
    </p>
    <p>
        3. Raahaa hiirtä pitämällä vasen painike pohjassa ja määritä alue.
    </p>
    <p>
        4. Avautuu uusi välilehti, josta voit tallentaa tiedoston.
    </p>
    <h2>
    </h2>
    <h2>
        5. Tulosta
    </h2>
    <p>
        Tulostaminen onnistuu joko karttanäkymän yläpalkin pikakuvakkeen <Icon icon="print" size="large"/> tai ”Kartat &amp; Työkalut” valikon kautta. Tulosteet tulevat
        aina pdf-formaatissa.
    </p>
    <p>
        Käyttöohje:
    </p>
    <p>
        1. Painetaan tulostus kuvaketta.
    </p>
    <p>
        2. Valikoidaan tulostettava alue liikuttamalla ja zoomaamalla
        karttanäkymää.
    </p>
    <p>
        3. Määritetään tulostusalue, mittakaava, resoluutio, rotaatio ja ristikko.
    </p>
    <p>
        4. Painetaan tulostus painiketta, jolloin pdf-tiedosto latautuu
        tallennettavaksi.
    </p>
    <h2>
    </h2>
    <h2>
        6. Jaa linkki
    </h2>
    <p>
        Jaa linkki löytyy ”Kartat &amp; Työkalut” valikosta. Avautuvasta
        välilehdestä voi jakaa kartan sosiaalisessa mediassa, kopioida selainlinkin
        tai QR-koodin.
    </p>
    <h2>
    </h2>
    <h2>
        7. Koordinaatisto
    </h2>
    <p>
        Koordinaatti- ja koordinaatistotiedot löytyvät karttanäkymän alaosassa.
        Koordinaattipalkki muodostuu kahdesta osasta: hiiren sijainnin
        koordinaateista ja koordinaattijärjestelmästä. Koordinaattien vierestä
        löytyy myös karttanäkymän mittakaava. Vasemmasta alakulmasta löytyy kartan
        mittakaavatieto.
    </p>

    <br />
    <h1 id="svenska">
        Swecos karttjänst
    </h1>
    <p>
        Karttjänstens version: <b>{process.env.BuildDate}</b>
    </p>
    <p>
		I karttjänsten kan du kombinera information och egenskaper med hjälp av 
		kartlagren, skapa dina egna kartvyer till exempel i stil med en temakarta, 
		dela och spara dem, samt utföra enkla analyser. Du kan använda samma länk 
		både för desktop och mobilversionerna. För närvarande kan du bara ändra 
		språket för tjänsten i webbläsarens inställningar. Du uppnår det bästa
		resultatet genom att använda den senaste versionen av Google Chrome.
    </p>
    <h1>
        Anvisningar
    </h1>
    <b>
        Ifall du har problem med programmet, vänligen kontakta oss!
    </b>
    <p>
        Jouko Pääkkölä +358 400740446
    </p>
    <p>
        Markku Nikula +358 407733851
    </p>
    <p>
        Jari Jaakkola +358 503160085
    </p>
    <p>
        förnamn.efternamn@sweco.fi
    </p>
    <h2>
        Manualens innehåll
    </h2>
    <p>
        1. Kartvyn
    </p>
    <p>
        2. Sökfunktionen
    </p>
    <p>
        3. Kartlagren
    </p>
    <p>
        4. Kartverktygen
    </p>
    <p>
        5. Skriv ut
    </p>
    <p>
        6. Dela länken
    </p>
    <p>
        7. Koordinatsystemet
    </p>
    <h2>
        1. ​Kartvyn
    </h2>
    <p>
        ​[Kuva käyttöliittymästä ja nuolet osoittamaan tärkeimpiin ominaisuuksiin]
    </p>
    <p>
		I kartvyns översta fält hittar du sökfunktionen för platser samt de vanligaste
		verktygen. I det nedre högra hörnet hittar du zoom-knapparna och hem-knappen, 
		som för dig tillbaka till startskärmen. Länken med ett platsnamn i det nedre 
		högra hörnet för dig till stadens/kommunens hemsida. I det övre vänstra hörnet
		finns "Kartor & Verktyg" sektionen, som innehåller kartlagren, delning av länk,
		bokmärken, kartverktyg samt utskrivning- och hjälp-knappar. Pilknappen i det 
		övre vänstra hörnet öppnar kartlagerlistan. I kartvyns nedersta fält finns ett 
		grått fält, som innehåller koordinat- och skalainformation samt användarvillkor.
    </p>
    <p>
        <strong>Navigerande på kartan</strong>
        <strong></strong>
    </p>
    <p>
        För att flytta på kartan, klicka på kartvyn och dra musen.
	
    </p>
    <p>
        <strong>Leta upp, zooma in, zooma ut eller återgå till startskärmen</strong>
        <strong></strong>
    </p>
    <p>
        GPS-positionen för enheten fås fram från knappen Lokalisera. 
       <Icon icon="plus" size="large"/>
    </p>
    <p>
        Om du rullar med musen kan du zooma in eller ut i kartvyn.
    </p>
    <p>
        Ett annat alternativ är att använda ikonen <Icon icon="plus" size="large"/> eller
        <Icon icon="minus" size="large"/> för att zooma in eller ut.
    </p>
    <p>
        Hem-knappen <Icon icon="home" size="large"/> för dig tillbaka till startsidan.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Klicka på kartvyn</strong>
        <strong></strong>
    </p>
    <p>
        Använd vänstra musknappen för att visa objektinformationen.
    </p>
    <p>
        Använd högra musknappen för att visa koordinaterna för kursorn.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Bytande av kartlager</strong>
        <strong></strong>
    </p>
    <p>
        Kartlagren finns bakom ikonen [layers and legend].
    </p>
    <p>
        Du kan aktivera och/eller dölja kartlager vid beskrivningen av lagret.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Sökfunktionen för platser</strong>
        <strong></strong>
    </p>
    <p>
		Kartvyns övre fält har en sökfunktion, där du kan söka efter platser.
    </p>
    <p>
		Vyn zoomas in till platsen efter sökningen.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Mätning</strong>
        <strong></strong>
    </p>
    <p>
        För att mäta avstånd, använd ikonen <Icon icon="measure_line" size="large"/>.
    </p>
    <p>
        För att mäta arean av ytor, använd ikonen <Icon icon="measure_polygon" size="large"/>.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Koordinaterna</strong>
        <strong></strong>
    </p>
    <p>
		I kartvyns nedre fält syns koordinaterna, koordinatsystemet och skalan.
    </p>
    <p>
		Genom att högerklicka med musen, öppnas koordinaterna i GK- och WSG84 
		-koordinatsystemen för kursorns plats.
    </p>
    <p>
		Mera information om verktygen finns i manualens del 4. Kartverktygen.
    </p>
    <h2>
        2. Sökning
    </h2>
    <p>
		Sökfunktionen finns i kartvyns övre fält. Sökbara platser kan vara t.ex. 
		vägar, skolor eller idrottsplatser.
    </p>
    <p>
        1. Systemet ger förslag på platser om du skriver ett sökord i fältet.
    </p>
    <p>
        2. Om du väljer en plats i sökfunktionen zoomas kartvyn in på platsen du sökt efter.
    </p>
    <h2>
        3. Kartlagren
    </h2>
    <p>
        Kartlagren kan nås via kartvyns vänstra övre hörn, antignen direkt via pilformade ikonen <Icon icon="karttatasot-nuoli" size="medium"/> 
		eller via ”Kartor &amp; Verktyg” -menyn. Lager kan aktiveras (smältas) och döljas (frysas)
        med ”kryss i rutan” -metoden. Lagrens plats och genomskinlighet kan kontrolleras genom
        <Icon icon="cog" size="large"/> -ikonen. I samma plats vinns tilläggsinformation
        gällande lagret. Dom valda lagren syns på kartan och dom aktiva lagren kan filtreras genom
        <Icon icon="eye" size="large"/> -ikonen.
    </p>
    <p>
        <strong></strong>
    </p>
    <p>
        <strong>Bytande av kartlager</strong>
    </p>
    <p>
        1. Öppna kartlager-fliken genom <Icon icon="karttatasot-nuoli" size="medium"/> ikonen eller via Kartor
        &amp; Verktyg” menyn.
    </p>
    <p>
        2. Aktivera eller dölj ett lager genom "kryss i rutan" -metoden genom att trycka på rutan vänster om materialet.
    </p>
    <p>
        <strong>Hantering av kartlager</strong>
    </p>
    <p>
        1. Välj och aktivera ett lager.
    </p>
    <p>
		2. På högra sidan av lagret finns <Icon icon="cog" size="large"/> -ikonen, som
		öppnar kontrollen för genomskinlighet. Lagret kan flyttas upp eller ner i listan
		med pilknapparna och tilläggsinformation om lagret får via infoknappen.
    </p>
    <p>
        3. Valda materialen kan också hanteras via <Icon icon="eye" size="large"/> -ikonen.
    </p>
    <h2>
        4. Kartverktygen.
    </h2>
    <p>
		Kartverktygen finns i "Kartor &amp; Verktyg" -menyn. I kartvyn hittar du
		en del av verktygen som ikoner i över fältet. Med hjälp av kartverktygen kan
		du begränsa området för att söka objekt/information, göra mätningar och 
		markeringar på kartan samt ladda ner material som rasterbilder.
    </p>
    <h3>
        <em><Icon icon="identify_region" size="large"/> Identifiera område:</em>
    </h3>
    <p>
		Med verktyget skapas en polygon vid valda området. Efter att polygonen har 
		skapats öppnas en mellanflik med platserna innanför polygonen, som kan 
		laddas ner i önskat format.
	
    </p>
    <p>
        Anvisningar:
    </p>
    <p>
        1. Aktivera verktyget.
    </p>
    <p>
        2. Vänsterklicka med musen för att markera ut polygonen.
    </p>
    <p>
        3. Dubbelklicka för att markera ut den sista punkten i polygonen.
    </p>
    <p>
        4. En mellanflik öppnas, som innehåller platsernas egenskapsinformation.
    </p>
    <h3>
        <em><Icon icon="measure" size="large"/> Mätning:</em>
    </h3>
    <p>
		Mätning -ikonen öppnar ett nytt fönster i kartvyns övredel. Genom att välja
		en av fyra olika verktyg, kan du placera ut koordinatpunkter och räkna avstånd,
		areor och riktningar.
    </p>
    <p>
        <em><strong>Läge</strong></em>
        <em>:</em>
    </p>
    <p>
		Räknar och konstruerar ett punktobjekt till kartvyn med koordinater.
    </p>
    <p>
        Anvisningar:
    </p>
    <p>
        1. Aktivera Läge -verktyget.
    </p>
    <p>
        2. Vänsterklicka på kartvyn.
    </p>
    <p>
        3. Ett punktobjekt med dess koordinater bildas i kartvyn.
    </p>
    <p>
        <em><strong>Längd</strong></em>
        <em>:</em>
    </p>
    <p>
		Konstruerar en streckad linje (polyline) och räknar dess totala längd.
		Dom enhet för längden kan väljas m, km, ft eller mi. I kartvyn visas 
		längden på enstaka segment och i verktygsmenyn hela längden för strecket.
    </p>
    <p>
        Anvisningar:
    </p>
    <p>
        1. Aktivera Längd -verktyget.
    </p>
    <p>
        2. Konstruera streckade linjens start- och vändpunkter genom att vänsterklicka med musen.
    </p>
    <p>
        3. Slutpunkten formas genom att dubbelklicka på kartvyn.
    </p>
    <p>
        <em><strong>Område</strong></em>
        <em>:</em>
    </p>
    <p>
		Med verktyget konstrueras en polygon, vars area räknas av systemet.
		Resultaten syns i verktygsmenyn samt i mitten av polygonen i kartvyn.
		Valbara enheter är m<sup>2</sup>, km<sup>2</sup>, ft<sup>2</sup> eller
        mi<sup>2</sup>.
    </p>
    <p>
        ​​​Anvisningar:
    </p>
    <p>
        1. Aktivera Område -verktyget.
    </p>
    <p>
        2. Konstruera start- och vändpunkterna för polygonen genom att vänsterklicka med musen.
    </p>
    <p>
        3. Slutpunkten formas genom att dubbelklicka på kartvyn.
    </p>
    <p>
        <em><strong>Riktning</strong></em>
        <em>:</em>
    </p>
    <p>
		Med verktyget formas ett streck, vars väderstreck räknas ut av systemet.
		Resultaten syns i verktygsmenyn samt i kartvyn.
    </p>
    <p>
        Anvisningar:
    </p>
    <p>
        1. Aktivera Riktning -verktyget.
    </p>
    <p>
        2. Streckets start- och slutpunkt konstrueras genom att vänsterklicka med musen.
    </p>
    <h3>
        <em><Icon icon="draw" size="large"/> Egna markeringar:</em>
    </h3>
    <p>
		Från verktygets fönster kan du lägga till egna markeringar till kartvyn.
		Med verktyget kan du rita olika geometrier eller skriva text, vars färg och 
		storlek kan definieras separat. Du kan konstruera nya lager från egna 
		markeringar, men du kan också editera färdiga lager.
    </p>
    <p>
        Anvisningar:
    </p>
    <p>
        1. Öppna Egna markeringar -verktyget.
    </p>
    <p>
        2. Definiera stilar och konstruera punkt-, streck-, område-, cirkel- eller textobjekt.
        Genom att editera kan du välja, ta bort eller göra en buffer till objektet.
    </p>
    <p>
        3.Via <Icon icon="plus" size="medium"/> -ikonen kan du göra ett nytt lager till objektet.
    </p>
    <h3>
        <em><Icon icon="rasterexport" size="large"/> Exportera rasterbilder</em>
    </h3>
    <p>

		Genom att definiera en rektangel i valbar storlek, konstrueras en rasterbild av 
		området i valbart format.
    </p>
    <p>
        Anvisningar:
    </p>
    <p>
        1. Öppna Rasterbild -verktyget, som får kartvyn att mörkna till.
    </p>
    <p>
        2. Välj format och resolution.
    </p>
    <p>
        3. Dra musen genom att hålla musens vänstra knapp i botten och definiera området.
    </p>
    <p>
        4. En nyt mellanflik öppnas, varifrån du kan spara filen.
    </p>
    <h2>
    </h2>
    <h2>
        5. Skriv ut
    </h2>
    <p>
		Utskrivning lyckas antingen via kartvyns <Icon icon="print" size="large"/> -ikon eller "Kartor &amp; Verktyg" menyn. 
		Utskriften kommer alltid i pdf-format.
    </p>
    <p>
        Anvisningar:
    </p>
    <p>
        1. Tryck på Skriv ut -ikonen.
    </p>
    <p>
        2. Välj utskrivbara området genom att röra på och zooma på kartvyn.
    </p>
    <p>
        3. Definiera utskriftsområdet, skalan, resolutionen, rotationen och rutnätet.
    </p>
    <p>
        4. Tryck på skriv ut -knappen för att spara pdf-filen för nedladdning.
    </p>
    <h2>
    </h2>
    <h2>
        6. Dela länken
    </h2>
    <p>
		Dela länken hittas i "Kartor &amp; Verktyg" menyn.
		Från fliken som öppnas kan du dela kartan i sociala media,
		kopiera webbläsarlänken eller QR-koden.
    </p>
    <h2>
    </h2>
    <h2>
        7. Koordinatsystemet
    </h2>
    <p>
		Koordinat- och koordinatsysteminformation finns längst ner i kartvyn. 
		Koordinatfältet består av två delar: koordinaterna för kursorns position och 
		koordinatsystemet. Bredvid koordinaterna finns även skalan för kartvyn. 
		Skalainformationen på kartan finns i det nedre vänstra hörnet.
    </p>


    <h2>LICENSE for QWC2</h2>
    <p>Copyright (c) 2015-2016 GeoSolutions Sas </p><p>Copyright (c) 2016-2021 Sourcepole AG</p><p>All rights reserved.</p>

<p>Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:</p>

<p> 1. Redistributions of source code must retain the above copyright notice, this list of  conditions and the following disclaimer.</p>

<p> 2. Redistributions in binary form must reproduce the above copyright notice, this list  of conditions and the following disclaimer in the documentation and/or other materials  provided with the distribution.</p>

<p>THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS &quot;AS IS&quot; AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.</p>

<p>The views and conclusions contained in the software and documentation are those of the authors and should not be interpreted as representing official policies, either expressed or implied, of the MapStore2 Project.</p>
</div>);
}
